import { useTranslation } from 'react-i18next';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useState } from 'react';
import {
  BD_NOTES,
  BD_OVERVIEW,
  sectionStatus,
  type BdNavGroup,
  type BdSectionStatus,
} from './bd-defs.js';
import type { BdArtifact, BdSavedArtifact } from '../../../lib/api/basic-design.js';

export interface BdSectionNavProps {
  /** テンプレートの部ごとのまとまり */
  groups: readonly BdNavGroup[];
  /** 保存済みの節（状態の印に使う） */
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** 開いている節か `BD_OVERVIEW` */
  selected: string;
  /** 節か `BD_OVERVIEW`・`BD_NOTES` を開く */
  onSelect: (key: string) => void;
  /** 畳んだまとまり（個人の見え方） */
  collapsed?: readonly string[];
  /** まとまりを畳む・開く。渡さなければ畳めない */
  onToggleGroup?: (group: string) => void;
  /** 節をまとまりの中で 1 つ前（-1）か後ろ（+1）へ動かす。渡さなければ並べ替えを出さない */
  onMove?: (group: string, artifact: BdArtifact, delta: -1 | 1) => void;
}

/** 状態の点の色。 @graph-connects none */
const DOT_CLASS: Record<BdSectionStatus, string> = {
  saved: 'bg-[var(--color-success)]',
  notApplicable: 'bg-[var(--color-info)]',
  notSaved: 'border border-[var(--color-border-hover)]',
};

/**
 * 基本設計の節のナビ。広い画面では左の一覧、狭い画面では選ぶ欄にする（出し分けは CSS の
 * ブレークポイントで行い、JS で画面幅を見ない）。まとまりはテンプレートの部、最後に全体
 * （図・変更一覧・マトリクス・書き出し）と共有のメモを置く。まとまりの畳みと節の並びは見ている人
 * ごとの見え方で、呼び出し側が持つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節をテンプレートの部ごとにまとめて並べ、状態の印つきで選ばせる（広い画面は一覧、狭い画面は選ぶ欄）
 * @graph-connects sectionStatus [calls] 節の状態
 */
export function BdSectionNav({
  groups,
  saved,
  selected,
  onSelect,
  collapsed = [],
  onToggleGroup,
  onMove,
}: BdSectionNavProps) {
  const { t } = useTranslation();
  const [arranging, setArranging] = useState(false);
  const overviewLabel = t('bd.groups.overview');
  const notesLabel = t('bd.notes.title');
  const options = [
    ...groups.flatMap((g) =>
      g.defs.map((def) => ({
        value: def.key,
        label: `${t(`bd.groups.${g.key}`)} · ${def.section} ${def.title}`,
      })),
    ),
    { value: BD_OVERVIEW, label: overviewLabel },
    { value: BD_NOTES, label: notesLabel },
  ];

  return (
    <>
      <div className="md:hidden">
        <Select
          size="sm"
          fullWidth
          aria-label={t('bd.nav.select')}
          options={options}
          value={selected}
          onChange={(e) => {
            onSelect(e.target.value);
          }}
        />
      </div>

      <nav aria-label={t('bd.nav.aria')} className="hidden md:block">
        {onMove === undefined ? null : (
          <button
            type="button"
            aria-pressed={arranging}
            className="mb-2 px-2 text-xs text-[var(--color-accent-primary)] hover:underline"
            onClick={() => {
              setArranging((v) => !v);
            }}
          >
            {arranging ? t('bd.nav.arrangeDone') : t('bd.nav.arrange')}
          </button>
        )}
        <ul className="space-y-4">
          {groups.map((group) => {
            const isCollapsed = collapsed.includes(group.key);
            const groupLabel = t(`bd.groups.${group.key}`);
            return (
              <li key={group.key}>
                {onToggleGroup === undefined ? (
                  <p className="px-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                    {groupLabel}
                  </p>
                ) : (
                  <button
                    type="button"
                    aria-expanded={!isCollapsed}
                    className="flex w-full items-center justify-between px-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                    onClick={() => {
                      onToggleGroup(group.key);
                    }}
                  >
                    <span>{groupLabel}</span>
                    <span aria-hidden="true">{isCollapsed ? '+' : '−'}</span>
                  </button>
                )}
                {isCollapsed ? null : (
                  <ul className="mt-1 space-y-0.5">
                    {group.defs.map((def, index) => (
                      <li key={def.key} className="flex items-center gap-1">
                        <NavButton
                          active={selected === def.key}
                          onClick={() => {
                            onSelect(def.key);
                          }}
                        >
                          <span
                            aria-hidden="true"
                            className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASS[sectionStatus(def, saved[def.key])]}`}
                          />
                          <span className="w-10 shrink-0 tabular-nums text-[var(--color-text-muted)]">
                            {def.section}
                          </span>
                          <span className="min-w-0 flex-1 truncate">{def.title}</span>
                        </NavButton>
                        {arranging && onMove !== undefined ? (
                          <span className="flex shrink-0">
                            <MoveButton
                              label={t('bd.nav.moveUp', { title: def.title })}
                              disabled={index === 0}
                              onClick={() => {
                                onMove(group.key, def.key, -1);
                              }}
                            >
                              ↑
                            </MoveButton>
                            <MoveButton
                              label={t('bd.nav.moveDown', { title: def.title })}
                              disabled={index === group.defs.length - 1}
                              onClick={() => {
                                onMove(group.key, def.key, 1);
                              }}
                            >
                              ↓
                            </MoveButton>
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
          <li className="space-y-0.5">
            <NavButton
              active={selected === BD_OVERVIEW}
              onClick={() => {
                onSelect(BD_OVERVIEW);
              }}
            >
              <span className="min-w-0 flex-1 truncate font-semibold">{overviewLabel}</span>
            </NavButton>
            <NavButton
              active={selected === BD_NOTES}
              onClick={() => {
                onSelect(BD_NOTES);
              }}
            >
              <span className="min-w-0 flex-1 truncate font-semibold">{notesLabel}</span>
            </NavButton>
          </li>
        </ul>
      </nav>
    </>
  );
}

/** 並べ替えのボタン。 @graph-connects none */
function MoveButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      className="rounded px-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] disabled:opacity-30"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** ナビの 1 項目。 @graph-connects none */
function NavButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      className={[
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs',
        active
          ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)]'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]',
      ].join(' ')}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
